import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Layers, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, remember);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Unable to sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="mf-fade-in w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white">
            <Layers className="h-8 w-8" />
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">ApparelFlow</h1>
          <p className="mt-2 text-lg text-slate-500">By Reminisce Tech</p>
        </div>

        <form
          onSubmit={submit}
          className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
          data-testid="login-form"
        >
          <h2 className="font-heading text-2xl font-bold text-slate-900">Sign in</h2>
          <p className="mt-1 text-base text-slate-500">Enter your email and password to continue.</p>

          {error && (
            <div data-testid="login-error" className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-base font-medium text-rose-700">
              {error}
            </div>
          )}

          <div className="mt-6 space-y-5">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-slate-600">Email address</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-12 w-full rounded-lg border border-slate-200 bg-white px-4 text-base text-slate-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-slate-600">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 w-full rounded-lg border border-slate-200 bg-white px-4 text-base text-slate-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2.5 text-base text-slate-600">
              <input
                data-testid="login-remember-checkbox"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              Remember me on this device
            </label>
          </div>

          <Button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="mt-7 h-12 w-full bg-blue-600 text-base font-semibold hover:bg-blue-700"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
